<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-container">
      <div class="filter-table-controls">
        <div class="filter-summary-container">
          <span>{{ activeDateString }}</span>
        </div>
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="220" :isOpen.sync="filterOpen">
          <div class="d-flex flex-column">
            <BoxContainer
              title="Date"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="dateFilterActive"
              @checkBoxClicked="dateFilterCheckBoxClicked"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <DateTimePicker
                placeholder="From"
                :value.sync="filterStartDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
              <DateTimePicker
                placeholder="To"
                :value.sync="filterEndDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
            </BoxContainer>
            <BoxContainer
              title="Job #"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="jobNoFilterActive"
              @checkBoxClicked="jobNoFilterCheckBoxClicked"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <Dropdown
                placeholder="Start Job #"
                :selectedObject.sync="filterStartJobNo"
                :isDisabled="!jobNoFilterActive || workshopJobsTableLoading"
                :errorMessage.sync="filterStartJobNoError"
                :items="EMSJobNoList"
                itemKey="JobNo"
                @selected="startJobNoSelected"
              />
              <Dropdown
                placeholder="End Job #"
                :selectedObject.sync="filterEndJobNo"
                :isDisabled="!jobNoFilterActive || workshopJobsTableLoading"
                :errorMessage.sync="filterEndJobNoError"
                :items="EMSJobNoList"
                itemKey="JobNo"
                @selected="endJobNoSelected"
              />
            </BoxContainer>
            <Button class="ml-auto mt-2" @click="filterRefreshClick">Refresh</Button>
          </div>
        </ButtonFilter>
      </div>
      <TableBox
        :tableData="workshopJobsTableData"
        :selectedRow.sync="workshopJobsTableSelectedRows"
        title="Workshop Jobs"
        :dataType="workshopJobsTableDataType"
        :loading="workshopJobsTableLoading"
        :maxSelection="-1"
        :hideColumns="['TimeIn', 'TimeOut']"
        style="min-height: 0"
        :showSelectAll="true"
      />
      <div style="display: flex; justify-content: end; width: 100%; margin-top: 10px">
        <Button style="width: fit-content" @click="previewClicked" :disabled="workshopJobsTableSelectedRows.length < 1"
          >Preview Work Order</Button
        ></div
      >
    </div>
    <div class="right-container">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        ref="designerV3"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
      />
      <div class="user-instructions" v-else>
        <span>Select some Workshop Jobs and click on <br /><strong>Preview Work Order</strong> to get started</span>
      </div>
    </div>

    <PopupV2
      class="email-popup"
      :show="showEmailPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailPopupButtons"
      title="Email Work Order"
      width="60%"
      @buttonClicked="emailPopupButtonClicked"
    >
      <div class="email-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    activeDateString() {
      if (this.filterActiveMode == 'Date')
        return `Date: ${new Date(this.activeStartDate).formatString('dd-MM-yyyy')} to ${new Date(this.activeEndDate).formatString('dd-MM-yyyy')}`;
      else if (this.filterActiveMode == 'JobNo') {
        return `Job No: ${this.activeStartJobNo} to ${this.activeEndJobNo}`;
      }
      return 'No Filter Active';
    },
  },
  data: function () {
    return {
      filterOpen: false,
      dateFilterActive: true,
      jobNoFilterActive: false,
      filterActiveMode: 'Date',

      defaultMonthsToShow: 2,

      activeStartDate: new Date(new Date().setMonth(new Date().getMonth() - 2, 1)),
      activeEndDate: new Date(),
      filterStartDate: new Date(new Date().setMonth(new Date().getMonth() - 2, 1)),
      filterEndDate: new Date(),

      activeStartJobNo: '',
      activeEndJobNo: '',
      filterStartJobNo: null,
      filterEndJobNo: null,
      filterStartJobNoError: '',
      filterEndJobNoError: '',

      EMSJobNoList: [],

      //workshop table items
      workshopJobsTableData: [],
      workshopJobsTableSelectedRows: [],
      workshopJobsTableDataType: {
        '#': 'number',
        'Job #': 'string',
        'Job Date ': 'datetime',
        'Inventory #': 'string',
        'Inventory Code': 'string',
        'Repair Type ': 'string',
        'Internal / External': 'string',
        'Requested By ': 'string',
        'Workshop ': 'string',
        'Service Person ': 'string',
        'Mileage (KM)': 'number',
        Remark: 'string',
        'Date/Time In': 'datetime',
        'Date/Time Out': 'datetime',
        'Trailer # ': 'string',
        'Last Update Time': 'datetime',
        'Last Update UserID ': 'string',
        'Create Time': 'datetime',
        'Create UserID ': 'string',
      },
      workshopJobsTableLoading: true,

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      showEmailPopup: false,
      emailPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailPopupToMails: [],
      emailPopupCCMails: [],
      emailPopupBCCMails: [],
      emailPopupSubjectInputValue: '',
      emailPopupMessageText: '',

      employeeList: [],
    };
  },
  props: {},
  methods: {
    async getWorkshopJobs() {
      this.workshopJobsTableLoading = true;
      this.workshopJobsTableData = [];
      this.workshopJobsTableSelectedRows = [];

      this.getEMSJobNoList();

      const joinDateTime = (date, time) => {
        if (!time) return date;
        const timeAdd = `${time.slice(0, 2)}:${time.slice(2, 5)}`;
        return `${date.slice(0, -5)}${timeAdd}`;
      };

      const filterBody = () => {
        const filterMode = this.filterActiveMode;

        if (filterMode == 'Date') {
          return {
            startdate: new Date(this.activeStartDate).formatString('yyyy-MM-dd'),
            enddate: new Date(this.activeEndDate).formatString('yyyy-MM-dd'),
          };
        } else if (filterMode == 'JobNo') {
          return {
            startjobno: this.activeStartJobNo,
            endjobno: this.activeEndJobNo,
          };
        }
      };

      this.workshopJobsTableData = (await this.$apiCallV2('/WorkshopJob/GetEMSJobList', filterBody())).map((item) => {
        const {
          'Date / Time In': dateTimeIn,
          TimeIn,
          'Date / Time Out': dateTimeOut,
          TimeOut,
          'Create Time': CreateTime,
          'Create UserID': CreateUserID,
          'Last Update Time': UpdateTime,
          'Last Update UserID': UpdateUserID,
        } = item;
        const dateTimeInFormat = dateTimeIn ? joinDateTime(dateTimeIn, TimeIn) : null;
        const dateTimeOutFormat = dateTimeOut ? joinDateTime(dateTimeOut, TimeOut) : null;
        return {
          ...item,
          'Date / Time In': dateTimeInFormat,
          'Date / Time Out': dateTimeOutFormat,
          'Create Time': CreateTime || UpdateTime,
          'Create UserID': CreateUserID || UpdateUserID,
        };
      });
      this.workshopJobsTableLoading = false;
    },

    async filterRefreshClick() {
      this.filterStartJobNoError = '';
      this.filterEndJobNoError = '';

      if (this.dateFilterActive) {
        if (!this.filterEndDate) {
          this.filterEndDate = new Date();
        }
        if (!this.filterStartDate || this.filterStartDate >= this.filterEndDate) {
          this.filterStartDate = new Date(
            new Date(this.filterEndDate).setMonth(
              new Date(this.filterEndDate).getMonth() - this.defaultMonthsToShow,
              1,
            ),
          );
        }
        this.filterActiveMode = 'Date';
        this.activeStartDate = this.filterStartDate;
        this.activeEndDate = this.filterEndDate;
      } else if (this.jobNoFilterActive) {
        if (!this.filterStartJobNo && !this.filterEndJobNo) {
          this.filterStartJobNoError = 'Start Job # is required';
          this.filterEndJobNoError = 'End Job # is required';
          return;
        }

        if (!this.filterStartJobNo) {
          this.filterStartJobNo = this.filterEndJobNo;
        }
        if (!this.filterEndJobNo) {
          this.filterEndJobNo = this.filterStartJobNo;
        }

        this.filterActiveMode = 'JobNo';
        this.activeStartJobNo = this.filterStartJobNo.JobNo;
        this.activeEndJobNo = this.filterEndJobNo.JobNo;
      }

      this.filterOpen = false;

      this.workshopJobsTableSelectedRows = [];
      await this.getWorkshopJobs();
    },

    dateFilterCheckBoxClicked(e) {
      this.jobNoFilterActive = !e;
    },

    jobNoFilterCheckBoxClicked(e) {
      this.dateFilterActive = !e;
    },

    startJobNoSelected() {
      const findStartIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterStartJobNo.JobNo);
      const findEndIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterEndJobNo?.JobNo);

      if (findEndIndex > findStartIndex) this.filterEndJobNo = null;
    },

    endJobNoSelected() {
      const findStartIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterStartJobNo?.JobNo);
      const findEndIndex = this.EMSJobNoList.findIndex((jobno) => jobno.JobNo == this.filterEndJobNo.JobNo);

      if (findEndIndex > findStartIndex) this.filterStartJobNo = null;
    },

    async getEMSJobNoList() {
      this.EMSJobNoList = await this.$apiCallV2('/WorkshopJob/GetEMSJobNoList', {});
    },

    async previewClicked() {
      if (this.workshopJobsTableSelectedRows.length < 1) {
        this.$showAlert('error', 'No Workshop Job Selected', 'Please select at least one Workshop Job to proceed');
        return;
      }

      this.$fullscreenLoading('Getting Work Order...');

      const workshopjob = this.workshopJobsTableSelectedRows.map((item) => item['Job #']).join(',');
      const requestBody = { workshopjob };

      const result = await this.$apiCallV2('/WorkshopJob/PrintJobOrder', requestBody);
      this.$fullscreenLoading();
      const workOrders = this.convertToArray(result.PrintEMSJob.EMSJob);

      const reportDate = () => {
        const date = new Date();

        // Day, Month, Year
        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const yyyy = date.getFullYear();

        // Hours, Minutes, Seconds
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        // AM/PM
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // convert 0 -> 12
        const hh = String(hours).padStart(2, '0');

        return `${dd}/${MM}/${yyyy} ${hh}:${minutes}:${seconds} ${ampm}`;
      };

      const designerFiles = {
        jobs: workOrders.map((workOrder) => {
          const {
            JobNo,
            JobDate,
            DateIn,
            TimeIn,
            DateOut,
            TimeOut,
            InventoryNo,
            VehicleCode,
            VehicleCategory,
            CategoryDesc,
            MeterReading,
            ServicePerson,
            Workshop,
            WorkshopName,
            Remark1,
            Tasks,
            Parts,
            RequestBy,
          } = workOrder;

          const requestedByPerson = this.employeeList.find((emp) => emp.EmployeeID == RequestBy);
          const { 'Employee Name': EmployeeName } = requestedByPerson || {};

          const tasksArray = this.convertToArray(Tasks);
          const partsArray = this.convertToArray(Parts);

          const totalCost = partsArray.reduce((sum, part) => sum + (Number(part.Cost) || 0), 0);
          return {
            page: '{{jsreportPageNum}} of {{jsreportTotalPage}}',

            jobNo: JobNo,
            requestedBy: EmployeeName,
            jobDate: new Date(JobDate).formatString('dd/MM/yyyy'),
            dateIn: DateIn ? new Date(DateIn).formatString('dd/MM/yyyy') : '',
            timeIn: DateIn ? TimeIn : '',
            dateOut: DateOut ? new Date(DateOut).formatString('dd/MM/yyyy') : '',
            timeOut: DateOut ? TimeOut : '',
            inventoryNo: InventoryNo,
            inventoryCode: VehicleCode,
            vehicleCat: `[${VehicleCategory}]` ?? '',
            categoryDesc: CategoryDesc ?? '',
            meterReading: MeterReading ?? '',
            servicePerson: ServicePerson ?? '',
            workshop: Workshop ? `[${Workshop}] ${WorkshopName}` : '',
            remark1: Remark1 ?? '',
            serviceListEnable: tasksArray.length > 0,
            serviceItem: tasksArray.map((task) => {
              const {
                ServiceDescription,
                TaskDescription,
                ServicePerson: taskServicePerson,
                DateStart: taskDateStart,
                TimeStart: taskTimeStart,
                DateEnd: taskDateEnd,
                TimeEnd: taskTimeEnd,
                Remark: serviceRemark,
              } = task;
              return {
                serviceDescription: ServiceDescription,
                taskDescription: TaskDescription,
                taskServicePerson: taskServicePerson,
                serviceStart:
                  taskDateStart ? `${new Date(taskDateStart).formatString('dd/MM/yyyy')} ${taskTimeStart}` : '',
                serviceEnd: taskDateEnd ? `${new Date(taskDateEnd).formatString('dd/MM/yyyy')} ${taskTimeEnd}` : '',
                serviceRemark: serviceRemark ? `Remarks: ${serviceRemark}` : '',
              };
            }),
            partListEnable: partsArray.length > 0,
            partItem: partsArray.map((part) => {
              const {
                PartDescription,
                PartType,
                PartNo,
                SerialNo,
                Size,
                DatePurchase,
                Quantity,
                Cost,
                Remark: partRemark,
              } = part;
              return {
                partDesc: PartDescription,
                partType: PartType,
                partNo: PartNo,
                serialNo: SerialNo,
                size: Size,
                datePurchase: DatePurchase ? new Date(DatePurchase).formatString('dd/MM/yyyy') : '',
                qty: Quantity,
                unitPrice: Quantity && Quantity > 0 ? (Cost / Quantity).toFixed(2) : '0.00',
                cost: Cost ? Number(Cost).toFixed(2) : '0.00',
                partRemark: partRemark ? `Remarks: ${partRemark}` : '',
              };
            }),
            totalCost: totalCost.toFixed(2),
          };
        }),
        general: {
          companyName: this.$store.state.loginDetails.CoName,
          reportDate: reportDate(),
        },
      };

      this.designerV3ReportFiles = Array.from({ length: designerFiles.jobs.length }, () => 'WorkOrder.json');
      this.designerV3ReportData = designerFiles.jobs;
      this.designerV3SharedData = designerFiles.general;
      this.showDesignerV3 = true;
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailPopupToMails = [];
      }
      this.emailPopupCCMails = [];
      this.emailPopupBCCMails = [];

      this.emailPopupSubjectInputValue = 'Work Order';
      this.emailPopupMessageText = `Work Order: ${shareLink}\n\n`;

      this.showEmailPopup = true;
    },

    emailPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailPopup = false;
        return;
      }

      if (this.emailPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'WORKORDER',
            docno: this.designerV3ReportData[0]['jobNo'],
            docnofieldname: '',
            recipient: this.emailPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailPopupSubjectInputValue.trim(),
            message: this.emailPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/WorkshopJob/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },

    designerV3AfterPrint: async function () {
      const requestBody = {
        report: 'WORKORDER',
        jobarray: this.designerV3ReportData.map((job) => job['jobNo']).join(','),
      };
      const response = await this.$apiCallV2('/WorkshopJob/UpdatePrintCount', requestBody);
      if (response !== '') {
        this.$showAlert(
          'error',
          'Fail to update print count',
          response?.ErrMsg.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    async getEmployeeList() {
      this.employeeList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' });
    },

    async getDefaultMonthsToDisplay() {
      const result = await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EMS_JobFilterMonth');
      const { SettingsValue } = result[0] || {};
      if (!SettingsValue) return;
      this.defaultMonthsToShow = Number(SettingsValue);
      this.activeStartDate = new Date(new Date().setMonth(new Date().getMonth() - this.defaultMonthsToShow, 1));
      this.filterStartDate = new Date(new Date().setMonth(new Date().getMonth() - this.defaultMonthsToShow, 1));
    },
  },
  async mounted() {
    this.$topLoading(0.1);
    await Promise.all([this.getDefaultMonthsToDisplay(), this.getEmployeeList()]);
    await this.getWorkshopJobs();
    await this.$topLoading(1);

    const query = this.$route.query.job?.trim() ?? null;
    if (query) {
      this.jobNoFilterActive = true;
      this.dateFilterActive = false;
      this.filterStartJobNo = this.EMSJobNoList.find((job) => job['JobNo'] == query);
      await this.filterRefreshClick();
      this.$nextTick(() => {
        this.workshopJobsTableSelectedRows = [{ ...this.workshopJobsTableData[0], _rowid: 'row0' }];
      });
    }
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  padding: 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;
  > .left-container {
    height: 100%;
    width: 50%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;
  }

  > .right-container {
    height: 100%;
    width: 50%;
    margin-left: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;

    .user-instructions {
      font-size: 1.25rem;
      padding: 3rem;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
  }

  .filter-table-controls {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 5px;

    > .filter-summary-container {
      margin-right: 5px;
      align-content: center;
      text-align: end;
    }

    .filter-content {
      text-align: left;
      cursor: auto;

      .refresh-button {
        margin-top: 10px;
        display: flex;
        justify-content: flex-end;
      }
    }

    > .select-deselect-all-button {
      margin-left: 5px;
    }
  }
  .email-popup {
    .email-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
